import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Github, Globe, Linkedin, Mail, MapPin, Phone } from 'lucide-react';
import PublicContactSection from '../shared/public-contact-section';
import { SkillProjectsPopup, useSkillProjects } from '../../components/skill-projects-popup';
import {
  Page, TopBar, BarBrand, BarNav, BarLink, BarAction, Shell, Hero, Eyebrow, HeroName,
  HeroBio, HeroActions, ActionLink, HeroCard, HeroPortrait, HeroMonogram, HeroFacts, Fact,
  Ticker, TickerTrack, Section, SectionLabel, SectionBody, RowList, Row, RowHead, RowTitle,
  RowMeta, RowDate, RowCopy, InlineLink, SkillGrid, SkillChip, ProjectGrid, ProjectCard,
  ProjectCover, ProjectBody, TagRow, ProjectLinks, ProjectLink, SplitGrid, ContactWrap,
  Footer, FooterInner, FooterName, FooterLinks, FooterLink, FooterNote, palette,
} from './bandara-template.styles';

const SOCIAL_ICONS = { github: Github, linkedin: Linkedin };

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function dateRange(start, end) {
  const startLabel = formatDate(start);
  const endLabel = formatDate(end);
  if (!startLabel && !endLabel) return '';
  return `${startLabel || '—'} — ${endLabel || 'Present'}`;
}

function initialsOf(name) {
  return String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '—';
}

function extraLinks(links) {
  if (!links || typeof links !== 'object') return [];
  return Object.entries(links)
    .filter(([key, value]) => value && typeof value === 'string' && !['github', 'linkedin', 'email'].includes(key.toLowerCase()))
    .map(([key, value]) => ({
      key,
      label: key.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
      url: value,
    }));
}

function projectLinksOf(project) {
  const links = Array.isArray(project?.links) ? project.links.filter((link) => link?.url) : [];
  if (project?.github_url) links.push({ url: project.github_url, title: 'Code' });
  if (project?.live_url) links.push({ url: project.live_url, title: 'Live' });
  return links;
}

function Portrait({ profile }) {
  const picture = typeof profile?.picture === 'string' ? profile.picture.trim() : '';
  const [hasImage, setHasImage] = useState(Boolean(picture));
  const name = profile?.profile_name || profile?.username;

  if (!hasImage) return <HeroMonogram aria-hidden="true">{initialsOf(name)}</HeroMonogram>;
  return <HeroPortrait src={picture} alt={name || 'Profile'} onError={() => setHasImage(false)} />;
}

function Cover({ project }) {
  const [hasImage, setHasImage] = useState(Boolean(project?.cover_image_url));

  return (
    <ProjectCover>
      {hasImage ? (
        <img
          src={project.cover_image_url}
          alt={project.title || 'Project cover'}
          onError={() => setHasImage(false)}
        />
      ) : (
        <span>{project?.title || 'Project'}</span>
      )}
    </ProjectCover>
  );
}

export default function BandaraTemplate({ data }) {
  const navigate = useNavigate();
  const { selectedSkill, isPopupVisible, handleSkillClick, handleClosePopup } = useSkillProjects();

  if (!data) return null;

  const profile = data.profile || {};
  const experiences = Array.isArray(data.experiences) ? data.experiences.filter(Boolean) : [];
  const educations = Array.isArray(data.educations) ? data.educations.filter(Boolean) : [];
  const skills = Array.isArray(data.skills) ? data.skills.filter((skill) => skill?.name) : [];
  const projects = Array.isArray(data.projects) ? data.projects.filter(Boolean) : [];
  const certifications = Array.isArray(data.certifications) ? data.certifications.filter(Boolean) : [];
  const organisations = Array.isArray(data.organisations) ? data.organisations.filter(Boolean) : [];

  const displayName = profile.profile_name || profile.username || 'Portfolio';
  const location = [profile.city, profile.country].filter(Boolean).join(', ');
  const socials = extraLinks(profile.links);
  const getKey = (item, index) => item?.id || `${item?.name || item?.title || 'entry'}-${index}`;

  const sections = [
    experiences.length > 0 && { id: 'experience', label: 'Experience' },
    projects.length > 0 && { id: 'projects', label: 'Projects' },
    skills.length > 0 && { id: 'skills', label: 'Skills' },
    educations.length > 0 && { id: 'education', label: 'Education' },
  ].filter(Boolean);

  const tickerItems = skills.length > 0
    ? skills.map((skill) => skill.name)
    : [displayName, 'Portfolio', 'Open to work'];

  return (
    <Page>
      <TopBar>
        <BarBrand href="#top">{displayName}</BarBrand>
        <BarNav aria-label="Portfolio sections">
          {sections.map((section) => (
            <BarLink key={section.id} href={`#${section.id}`}>{section.label}</BarLink>
          ))}
        </BarNav>
        <BarAction href="#contact">Get in touch</BarAction>
      </TopBar>

      <Shell id="top">
        <Hero>
          <div>
            <Eyebrow>{location ? `${location} — Portfolio` : 'Portfolio'}</Eyebrow>
            <HeroName><span>{displayName}</span></HeroName>
            {profile.bio && <HeroBio>{profile.bio}</HeroBio>}
            <HeroActions>
              {profile.email && (
                <ActionLink $solid href={`mailto:${profile.email}`}>
                  <Mail size={16} /> Email me
                </ActionLink>
              )}
              {profile.resume_url && (
                <ActionLink href={profile.resume_url} target="_blank" rel="noreferrer">
                  Resume <ArrowUpRight size={16} />
                </ActionLink>
              )}
              {profile.github && (
                <ActionLink href={profile.github} target="_blank" rel="noreferrer">
                  <Github size={16} /> GitHub
                </ActionLink>
              )}
              {profile.linkedin && (
                <ActionLink href={profile.linkedin} target="_blank" rel="noreferrer">
                  <Linkedin size={16} /> LinkedIn
                </ActionLink>
              )}
            </HeroActions>
          </div>

          <HeroCard>
            <Portrait profile={profile} />
            <HeroFacts>
              {profile.email && (
                <Fact>
                  <dt>Email</dt>
                  <dd><a href={`mailto:${profile.email}`}>{profile.email}</a></dd>
                </Fact>
              )}
              {profile.phone && (
                <Fact>
                  <dt>Phone</dt>
                  <dd><a href={`tel:${profile.phone}`}>{profile.phone}</a></dd>
                </Fact>
              )}
              {location && (
                <Fact>
                  <dt>Based in</dt>
                  <dd>{location}</dd>
                </Fact>
              )}
              {socials.length > 0 && (
                <Fact>
                  <dt>Elsewhere</dt>
                  <dd>
                    {socials.map((link) => (
                      <div key={link.key}>
                        <a href={link.url} target="_blank" rel="noreferrer">{link.label}</a>
                      </div>
                    ))}
                  </dd>
                </Fact>
              )}
            </HeroFacts>
          </HeroCard>
        </Hero>
      </Shell>

      <Ticker aria-hidden="true">
        <TickerTrack>
          <span>{tickerItems.join(' + ')}</span>
          <span>{tickerItems.join(' + ')}</span>
        </TickerTrack>
      </Ticker>

      <Shell>
        {experiences.length > 0 && (
          <Section id="experience">
            <SectionLabel>
              <span>01</span>
              <h2>Experience</h2>
            </SectionLabel>
            <SectionBody>
              <RowList>
                {experiences.map((exp, index) => (
                  <Row key={getKey(exp, index)}>
                    <RowHead>
                      <div>
                        <RowTitle>{exp.role || exp.name}</RowTitle>
                        {[exp.company, exp.location].filter(Boolean).length > 0 && (
                          <RowMeta>{[exp.company, exp.location].filter(Boolean).join(' · ')}</RowMeta>
                        )}
                      </div>
                      {dateRange(exp.start_date, exp.end_date) && (
                        <RowDate>{dateRange(exp.start_date, exp.end_date)}</RowDate>
                      )}
                    </RowHead>
                    {exp.description && <RowCopy>{exp.description}</RowCopy>}
                  </Row>
                ))}
              </RowList>
            </SectionBody>
          </Section>
        )}

        {projects.length > 0 && (
          <Section id="projects">
            <SectionLabel>
              <span>02</span>
              <h2>Projects</h2>
            </SectionLabel>
            <SectionBody>
              <ProjectGrid>
                {projects.map((project, index) => (
                  <ProjectCard
                    key={getKey(project, index)}
                    $clickable={Boolean(project.id)}
                    onClick={() => project.id && navigate(`/projects/${project.id}`)}
                  >
                    <Cover project={project} />
                    <ProjectBody>
                      <RowTitle>{project.title || 'Untitled project'}</RowTitle>
                      {project.description && (
                        <RowCopy style={{ marginTop: 0 }}>
                          {project.description.length > 160
                            ? `${project.description.slice(0, 160)}...`
                            : project.description}
                        </RowCopy>
                      )}
                      {Array.isArray(project.skills) && project.skills.length > 0 && (
                        <TagRow>
                          {project.skills.slice(0, 5).map((skill, skillIndex) => (
                            <li key={`${getKey(project, index)}-skill-${skillIndex}`}>
                              {typeof skill === 'string' ? skill : skill?.name}
                            </li>
                          ))}
                        </TagRow>
                      )}
                      {projectLinksOf(project).length > 0 && (
                        <ProjectLinks>
                          {projectLinksOf(project).map((link, linkIndex) => (
                            <ProjectLink
                              key={`${getKey(project, index)}-link-${linkIndex}`}
                              href={link.url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {link.title || 'Open'} <ArrowUpRight size={14} />
                            </ProjectLink>
                          ))}
                        </ProjectLinks>
                      )}
                    </ProjectBody>
                  </ProjectCard>
                ))}
              </ProjectGrid>
            </SectionBody>
          </Section>
        )}

        {skills.length > 0 && (
          <Section id="skills">
            <SectionLabel>
              <span>03</span>
              <h2>Skills</h2>
            </SectionLabel>
            <SectionBody>
              <SkillGrid>
                {skills.map((skill, index) => (
                  <SkillChip key={getKey(skill, index)} type="button" onClick={() => handleSkillClick(skill)}>
                    {skill.name}
                  </SkillChip>
                ))}
              </SkillGrid>
            </SectionBody>
          </Section>
        )}

        {educations.length > 0 && (
          <Section id="education">
            <SectionLabel>
              <span>04</span>
              <h2>Education</h2>
            </SectionLabel>
            <SectionBody>
              <SplitGrid>
                {educations.map((edu, index) => (
                  <Row key={getKey(edu, index)}>
                    <RowTitle>{edu.degree || edu.name}</RowTitle>
                    {[edu.field_of_study, edu.university].filter(Boolean).length > 0 && (
                      <RowMeta>{[edu.field_of_study, edu.university].filter(Boolean).join(' · ')}</RowMeta>
                    )}
                    {dateRange(edu.start_date, edu.end_date) && (
                      <RowMeta>{dateRange(edu.start_date, edu.end_date)}</RowMeta>
                    )}
                  </Row>
                ))}
              </SplitGrid>
            </SectionBody>
          </Section>
        )}

        {certifications.length > 0 && (
          <Section id="certifications">
            <SectionLabel>
              <span>05</span>
              <h2>Certifications</h2>
            </SectionLabel>
            <SectionBody>
              <SplitGrid>
                {certifications.map((cert, index) => (
                  <Row key={getKey(cert, index)}>
                    <RowTitle>{cert.title || cert.name}</RowTitle>
                    {cert.issuer && <RowMeta>{cert.issuer}</RowMeta>}
                    {formatDate(cert.end_date) && <RowMeta>{formatDate(cert.end_date)}</RowMeta>}
                    {cert.credential_url && (
                      <InlineLink href={cert.credential_url} target="_blank" rel="noreferrer">
                        Credential <ArrowUpRight size={14} />
                      </InlineLink>
                    )}
                  </Row>
                ))}
              </SplitGrid>
            </SectionBody>
          </Section>
        )}

        {organisations.length > 0 && (
          <Section id="organisations">
            <SectionLabel>
              <span>06</span>
              <h2>Organisations</h2>
            </SectionLabel>
            <SectionBody>
              <RowList>
                {organisations.map((org, index) => (
                  <Row key={getKey(org, index)}>
                    <RowHead>
                      <div>
                        <RowTitle>{org.name || org.title}</RowTitle>
                        {org.role && <RowMeta>{org.role}</RowMeta>}
                      </div>
                      {dateRange(org.start_date, org.end_date) && (
                        <RowDate>{dateRange(org.start_date, org.end_date)}</RowDate>
                      )}
                    </RowHead>
                    {org.description && <RowCopy>{org.description}</RowCopy>}
                  </Row>
                ))}
              </RowList>
            </SectionBody>
          </Section>
        )}

        <ContactWrap id="contact">
          <PublicContactSection
            profile={profile}
            eyebrow="Contact"
            title="Start a conversation"
            subtitle="Messages land straight in the inbox of this portfolio's owner."
            palette={{
              surface: palette.paper,
              card: palette.canvas,
              input: palette.paper,
              border: palette.ink,
              text: palette.ink,
              muted: palette.muted,
              iconBg: palette.paper,
              iconFg: palette.accent,
              buttonBg: palette.ink,
              buttonText: palette.paper,
              shadow: `6px 6px 0 ${palette.ink}`,
            }}
          />
        </ContactWrap>
      </Shell>

      <Footer>
        <FooterInner>
          <FooterName>{displayName}</FooterName>
          <FooterLinks aria-label="Social links">
            {profile.email && (
              <FooterLink href={`mailto:${profile.email}`} aria-label="Email">
                <Mail size={18} />
              </FooterLink>
            )}
            {profile.phone && (
              <FooterLink href={`tel:${profile.phone}`} aria-label="Phone">
                <Phone size={18} />
              </FooterLink>
            )}
            {profile.github && (
              <FooterLink href={profile.github} target="_blank" rel="noreferrer" aria-label="GitHub">
                <Github size={18} />
              </FooterLink>
            )}
            {profile.linkedin && (
              <FooterLink href={profile.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
                <Linkedin size={18} />
              </FooterLink>
            )}
            {socials.map((link) => {
              const Icon = SOCIAL_ICONS[link.key.toLowerCase()] || Globe;
              return (
                <FooterLink key={link.key} href={link.url} target="_blank" rel="noreferrer" aria-label={link.label}>
                  <Icon size={18} />
                </FooterLink>
              );
            })}
            {location && (
              <FooterLink as="span" aria-label={`Based in ${location}`}>
                <MapPin size={18} />
              </FooterLink>
            )}
          </FooterLinks>
        </FooterInner>
        <FooterNote>&copy; {new Date().getFullYear()} {displayName}. All rights reserved.</FooterNote>
      </Footer>

      <SkillProjectsPopup
        visible={isPopupVisible}
        onHide={handleClosePopup}
        skill={selectedSkill}
      />
    </Page>
  );
}
